import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '@/api/client';
import { useOgAudienceEvents, useOgEventDetails } from '@/api/opengater/useEvents';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/primitives/Input';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import s from './EventAudiencePicker.module.css';
const HINT = 'Только зарегистрированные по любому из выбранных событий. Остальные фильтры тоже применяются. Пусто — без фильтра.';
const STATUS = {
    active: 'активная',
    hidden: 'неактивная',
    deleted: 'удалённая',
};
function isMissing(error) {
    return error instanceof ApiError && error.status === 404;
}
function CampaignTag({ id, event, missing, disabled, onRemove, }) {
    const name = event?.name ?? `Кампания #${id}${missing ? ' не найдена' : ''}`;
    return (_jsxs("span", { className: s.tag, "data-status": event?.status === 'active' ? undefined : event?.status, children: [_jsx("span", { className: s.tagName, children: name }), event && event.status !== 'active' && (_jsx("span", { className: s.tagStatus, children: STATUS[event.status] })), !disabled && (_jsx("button", { type: "button", className: s.remove, "aria-label": `Убрать кампанию ${name}`, onClick: onRemove, children: _jsx(Icon, { name: "x", size: 12 }) }))] }));
}
export function EventAudiencePicker({ value, disabled, onChange, }) {
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const optionRefs = useRef([]);
    const listboxId = useId();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [popupPosition, setPopupPosition] = useState({ visibility: 'hidden' });
    const query = useDebouncedValue(search, 300);
    const querySettling = search !== query;
    const catalog = useOgAudienceEvents(query, open && !querySettling);
    const detailQueries = useOgEventDetails(value);
    const events = useMemo(() => catalog.data?.pages.flatMap((page) => page.items) ?? [], [catalog.data?.pages]);
    const catalogById = useMemo(() => new Map(events.map((item) => [item.id, item])), [events]);
    const selected = value.map((id, index) => ({
        id,
        event: detailQueries[index]?.data ?? catalogById.get(id),
        missing: isMissing(detailQueries[index]?.error),
    }));
    const placePopup = useCallback(() => {
        const anchor = rootRef.current;
        if (!anchor)
            return;
        const rect = anchor.getBoundingClientRect();
        const inset = 16;
        const gap = 8;
        const width = Math.min(440, Math.max(0, window.innerWidth - inset * 2), Math.max(320, rect.width));
        const height = popupRef.current?.offsetHeight ?? 0;
        const belowSpace = Math.max(0, window.innerHeight - rect.bottom - gap - inset);
        const aboveSpace = Math.max(0, rect.top - gap - inset);
        const opensAbove = height > belowSpace && aboveSpace > belowSpace;
        const maxHeight = opensAbove ? aboveSpace : belowSpace;
        const visibleHeight = height > 0 ? Math.min(height, maxHeight) : maxHeight;
        const top = opensAbove ? rect.top - gap - visibleHeight : rect.bottom + gap;
        const maxLeft = Math.max(inset, window.innerWidth - width - inset);
        const left = Math.min(Math.max(inset, rect.right - width), maxLeft);
        const next = { top, left, width, maxHeight, visibility: 'visible' };
        setPopupPosition((current) => current.top === next.top &&
            current.left === next.left &&
            current.width === next.width &&
            current.maxHeight === next.maxHeight
            ? current
            : next);
    }, []);
    useEffect(() => {
        if (!open)
            return;
        const closeOutside = (event) => {
            const target = event.target;
            if (!rootRef.current?.contains(target) && !popupRef.current?.contains(target))
                setOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, [open]);
    useEffect(() => {
        if (disabled)
            setOpen(false);
    }, [disabled]);
    useLayoutEffect(() => {
        if (!open)
            return;
        placePopup();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => placePopup());
        if (popupRef.current)
            observer?.observe(popupRef.current);
        if (rootRef.current)
            observer?.observe(rootRef.current);
        window.addEventListener('resize', placePopup);
        document.addEventListener('scroll', placePopup, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', placePopup);
            document.removeEventListener('scroll', placePopup, true);
        };
    }, [open, placePopup]);
    const toggle = (id) => {
        if (value.includes(id))
            onChange(value.filter((selectedId) => selectedId !== id));
        else if (value.length < 1000)
            onChange([...value, id]);
    };
    const focusOption = (index) => {
        const options = optionRefs.current.filter((item) => item != null);
        if (!options.length)
            return;
        options[Math.min(Math.max(index, 0), options.length - 1)]?.focus();
    };
    const handleKeyDown = (event) => {
        if (event.key !== 'Escape' || !open)
            return;
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
    };
    return (_jsxs("div", { ref: rootRef, className: s.root, onKeyDown: handleKeyDown, children: [_jsxs("div", { className: s.control, "data-readonly": disabled || undefined, children: [selected.length > 0 ? (_jsx("div", { className: s.tags, children: selected.map((item) => (_jsx(CampaignTag, { ...item, disabled: disabled, onRemove: () => toggle(item.id) }, item.id))) })) : (_jsx("span", { className: s.empty, children: disabled ? 'Без фильтра по кампаниям' : 'Кампании не выбраны' })), !disabled && (_jsxs("button", { ref: triggerRef, type: "button", className: s.trigger, "aria-label": "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438", "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": open ? listboxId : undefined, onClick: () => {
                            setOpen((current) => !current);
                            if (!open)
                                setSearch('');
                        }, children: [_jsx("span", { children: selected.length ? 'Добавить' : 'Выбрать кампании' }), _jsx(Icon, { name: "chev-down", size: 14 })] }))] }), _jsx("p", { className: s.hint, children: HINT }), open &&
                !disabled &&
                createPortal(_jsxs("div", { ref: popupRef, className: s.popup, style: popupPosition, "data-testid": "event-audience-popup", children: [_jsxs("div", { className: s.search, children: [_jsx(Icon, { name: "search", size: 15 }), _jsx(Input, { value: search, autoFocus: true, "aria-label": "\u041F\u043E\u0438\u0441\u043A \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439", "aria-controls": listboxId, placeholder: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435, \u043A\u043E\u0434 \u0438\u043B\u0438 \u0441\u0441\u044B\u043B\u043A\u0430", onChange: (event) => setSearch(event.target.value), onKeyDown: (event) => {
                                        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
                                            return;
                                        event.preventDefault();
                                        focusOption(event.key === 'ArrowDown' ? 0 : optionRefs.current.length - 1);
                                    } })] }), catalog.isError ? (_jsxs("div", { className: s.message, children: [_jsx("span", { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438." }), _jsx("button", { type: "button", className: s.retry, onClick: () => void catalog.refetch(), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })] })) : querySettling || catalog.isLoading ? (_jsx("div", { className: s.message, children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438\u2026" })) : events.length === 0 ? (_jsx("div", { className: s.message, children: "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E" })) : (_jsx("div", { id: listboxId, role: "listbox", "aria-label": "\u041A\u0430\u043C\u043F\u0430\u043D\u0438\u0438", "aria-multiselectable": "true", className: s.list, children: events.map((item, index) => {
                                const checked = value.includes(item.id);
                                const atLimit = !checked && value.length >= 1000;
                                return (_jsxs("button", { ref: (node) => {
                                        optionRefs.current[index] = node;
                                    }, type: "button", role: "option", "aria-selected": checked, "aria-disabled": atLimit || undefined, disabled: atLimit, className: s.option, "data-selected": checked || undefined, onClick: () => toggle(item.id), onKeyDown: (event) => {
                                        let next = null;
                                        if (event.key === 'ArrowDown')
                                            next = index + 1;
                                        else if (event.key === 'ArrowUp')
                                            next = index - 1;
                                        else if (event.key === 'Home')
                                            next = 0;
                                        else if (event.key === 'End')
                                            next = optionRefs.current.length - 1;
                                        if (next == null)
                                            return;
                                        event.preventDefault();
                                        focusOption(next);
                                    }, children: [_jsx("span", { className: s.check, children: checked && _jsx(Icon, { name: "check", size: 14 }) }), _jsx("span", { className: s.optionName, children: item.name }), _jsx("span", { className: s.status, "data-status": item.status, children: STATUS[item.status] })] }, item.id));
                            }) })), catalog.hasNextPage && !catalog.isError && !querySettling && (_jsx("button", { type: "button", className: s.more, disabled: catalog.isFetchingNextPage, onClick: () => void catalog.fetchNextPage(), children: catalog.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё' }))] }), document.body)] }));
}
